import { ReadonlyVec3 } from 'gl-matrix';
import { RenderStorage } from '~/engine/core/render/RenderStorage';
import { ContourRenderer } from '~/engine/core/render/renderers/ContourRenderer';
import { TextureView } from '~/engine/view/TextureView';
import { ContourTexture } from '../../structure/ContourTexture';
import { Grid } from '../Grid';
import { CustomObject } from './CustomObject';

/** One coloured region of the comparison overlay (a precomputed SDF). */
export interface ComparisonOverlayRegion {
    /**
     * Region texture: the agreement intersection (A∩B) or a clone of an ROI's
     * own contour texture. Owned by this object.
     */
    texture: ContourTexture;
    /** GPU-normalised [0,1] fill/edge colour. */
    color: ReadonlyVec3;
    /** Fill opacity; 0 → outline only (each ROI's own contour). */
    fillAlpha: number;
    /** Edge / outline thickness. */
    thickness: number;
}

/**
 * Custom overlay that draws a colour-coded comparison between two ROIs:
 * the agreement region (A∩B) as a translucent green fill, and each ROI's own
 * contour as an outline in its own colour. Per-region fill opacity and
 * thickness arrive with each {@link ComparisonOverlayRegion}.
 *
 * The region textures are precomputed off the render path — in the React
 * bridge hook (the agreement via a validated SDF intersection, each ROI's
 * contour via a clone of its texture) — and handed in through
 * {@link setRegions}. This object only DRAWS them each frame using
 * the existing {@link ContourRenderer}, so it inherits the contour layer's
 * proven cross-section alignment and needs no bespoke shader.
 *
 * The object OWNS the textures it is given and frees them when they are
 * replaced or when the object is disposed.
 */
export class ComparisonObject extends CustomObject {
    /** Placeholder grid returned before any regions are set. */
    private static readonly EMPTY_GRID = Grid.empty();

    private _regions: ComparisonOverlayRegion[] = [];

    public constructor() {
        super();
    }

    /**
     * Required by the {@link EnvironmentObject} contract; this transient
     * overlay is not grid-bound, so it reports the first region's grid (or
     * an empty grid). The render loop never consults it.
     */
    public get grid(): Grid {
        return (
            this._regions[0]?.texture.textureGrid ?? ComparisonObject.EMPTY_GRID
        );
    }

    /** Replace the difference regions, freeing any previously-held textures. */
    public setRegions(regions: ComparisonOverlayRegion[]): void {
        this.deleteRegions();
        this._regions = regions;
    }

    public render(view: TextureView): void {
        if (this._regions.length === 0) {
            return;
        }
        const contourRenderer = RenderStorage.getRenderer(ContourRenderer);
        for (const region of this._regions) {
            contourRenderer.renderContourTexture(
                view,
                region.texture,
                region.color,
                region.thickness,
                { fillAlpha: region.fillAlpha }
            );
        }
    }

    public delete(): void {
        this.deleteRegions();
    }

    private deleteRegions(): void {
        for (const region of this._regions) {
            region.texture.delete();
        }
        this._regions = [];
    }
}
